import React, { useId } from "react";
import { t } from "ttag";

import type {
    ServerCmsTypesProductSummary as ProductSummary,
    RichTextStreamBlock as RichTextStreamValue,
    Spacer2026Block as SpacerValue,
    ProductCompareBlock as Value,
} from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import type { StreamFieldBlockMap } from "@thelabnyc/thelabui/src/components/StreamField";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Themed } from "../../styles/themes";
import { delegateClickToLink } from "../../utils/delegateClickToLink";
import { Carousel } from "../Carousel/Carousel";
import { Clickable } from "../Clickables";
import { FavoriteToggleButton } from "../FavoriteToggleButton";
import { ProductStatusBadge } from "../ProductStatusBadge";
import { RichTextStreamBlock } from "../RichTextStreamBlock";
import { SKSDots } from "../SKSCarousel/Dots";
import { ScrollNavMenuItemBlock } from "../ScrollNavMenuBlock";
import { SpacerBlock } from "../SpacerBlock";

import styles from "./index.module.scss";

interface Props {
    value: Value;
    products: ProductSummary[];
    /* Streamfield position; used as sortOrder for the scroll-nav registration
     * so the Comparison item appears in the right spot in the nav bar. */
    index?: number;
}

const slidesVisibleDesktop = 4;
const slidesVisibleTablet = 2.5;
const slidesVisibleMobile = 1.5;

/**
 * Rendered above the comparison table when the block's items don't include
 * an editor-provided rich_text_stream.
 */
const DefaultHeader = ({ headingId }: { headingId: string }) => {
    const richText: RichTextStreamValue = {
        content: [
            {
                id: headingId,
                type: "heading",
                value: {
                    text: `<p>${t`Choose the One for You`}</p>`,
                    tag: "h2",
                },
            },
            {
                id: `${headingId}-body`,
                type: "body",
                value: {
                    content: `<p>${t`Discover which SKS appliance is the best fit for your dream kitchen.`}</p>`,
                    width: "half",
                },
            },
        ],
        alignment: "center",
        content_size: "md",
        size_adjustment: "0",
        container_width: "width8",
    };
    const spacer: SpacerValue = {
        height: "--spacer-24",
        anchor: null,
        line_color: "gray-300",
        line_style: "none",
    };
    return (
        <>
            <RichTextStreamBlock value={richText} />
            <SpacerBlock value={spacer} />
        </>
    );
};

/* Placeholder so a missing image/price/facet still occupies its subgrid row
 * — keeps cross-card alignment intact. */
const Empty = () => <div />;

const CompareCard = ({
    product,
    isActive,
}: {
    product: ProductSummary;
    isActive: boolean;
}) => {
    const facets = product.featured_facets;
    const isDiscontinued = product.status === "discontinued";

    return (
        <Themed
            tag="article"
            theme={isActive ? "white" : null}
            className={concatClassNames([
                isActive ? styles.cardActive : styles.card,
                product.url ? styles.hasUrl : "",
            ])}
            onClick={delegateClickToLink}
        >
            {product.image ? (
                <ImageChooserBlock
                    value={product.image}
                    attrs={{ className: styles.picture }}
                    imageAttrs={{ className: styles.image, alt: "" }}
                />
            ) : (
                <Empty />
            )}

            <header className={styles.header}>
                <p className={styles.sku}>{product.sku}</p>
                <h3 className={styles.title}>
                    {product.url ? (
                        <Clickable href={product.url}>
                            {product.title}
                        </Clickable>
                    ) : (
                        product.title
                    )}
                </h3>
                <ProductStatusBadge
                    status={product.status}
                    className={styles.badgePosition}
                />
                {product.url && !isDiscontinued && (
                    <FavoriteToggleButton
                        product={{
                            name: product.title,
                            sku: product.sku,
                            url: product.url,
                        }}
                        image={product.image}
                        className={styles.favoriteButton}
                        svgClassName={styles.favoriteSvg}
                    />
                )}
            </header>

            {product.msrp && !isDiscontinued ? (
                <p className={styles.price}>{product.msrp.formatted}</p>
            ) : (
                <Empty />
            )}

            <dl className={styles.facetList}>
                {facets.map((facet) => (
                    <div
                        key={`${facet.facet_slug}-${facet.value_slug}`}
                        className={styles.facetItem}
                    >
                        <dt className={styles.facetLabel}>{facet.name}</dt>
                        <dd className={styles.facetValue}>
                            {facet.display_value}
                        </dd>
                    </div>
                ))}
            </dl>
        </Themed>
    );
};

const CompareTable = ({
    products,
    headingId,
}: {
    products: ProductSummary[];
    headingId: string | undefined;
}) => {
    const facetCount = Math.max(
        ...products.map((p) => p.featured_facets.length),
    );
    const carouselStyle = {
        "--slides-visible-desktop": slidesVisibleDesktop,
        "--slides-visible-tablet": slidesVisibleTablet,
        "--slides-visible-mobile": slidesVisibleMobile,
        "--compare-facet-count": facetCount,
    };
    /**
     * labelledBy points at the section heading (default or editor-provided);
     * label is the fallback for the case where richtext is in the stream
     * without a heading.
     */
    const labelProps = headingId
        ? ({ labelledBy: headingId } as const)
        : ({ label: t`Product Comparison` } as const);

    return (
        <div className={styles.root} style={carouselStyle}>
            <Carousel
                {...labelProps}
                slideWrapperProps={{ className: styles.slidesWrapper }}
                slideContainerProps={{ className: styles.slide }}
                controls={null}
                dots={(props) => <SKSDots {...props} className={styles.dots} />}
            >
                {products.map((product, index) => (
                    <CompareCard
                        key={product.sku}
                        product={product}
                        isActive={index === 0}
                    />
                ))}
            </Carousel>
        </div>
    );
};

export const ProductCompareBlock = ({ value, products, index }: Props) => {
    const defaultHeadingId = useId();
    const items = value.items ?? [];
    const renders = items.length > 0 && products.length >= 2;

    if (!renders) return null;

    const hasRichText = items.some((item) => item.type === "rich_text_stream");

    const headingId = hasRichText
        ? items
              .flatMap((item) =>
                  item.type === "rich_text_stream"
                      ? (item.value.content ?? [])
                      : [],
              )
              .find((b) => b.type === "heading")?.id
        : defaultHeadingId;

    const components: StreamFieldBlockMap<NonNullable<Value["items"]>> = {
        rich_text_stream: RichTextStreamBlock,
        spacer: SpacerBlock,
        compare_table: () => (
            <>
                {!hasRichText && <DefaultHeader headingId={defaultHeadingId} />}
                <CompareTable products={products} headingId={headingId} />
            </>
        ),
    };

    return (
        <Themed theme={value.theme} tag="section" className={styles.block}>
            <ScrollNavMenuItemBlock
                value={{ name: t`Comparison` }}
                index={index}
            />
            <StreamField
                tagName={React.Fragment}
                components={components}
                value={items}
            />
        </Themed>
    );
};
